import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { observer, inject } from 'mobx-react';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle, faEllipsisH, faSpinner, faPencilAlt, faCheck , faSync, faSort, faSortUp, faSortDown, faFilter, faCog } from "@fortawesome/free-solid-svg-icons";

import Utils from "../utils/utils";
import { readApiErrorCode } from "../utils/api-error";
import Modal from '../components/modal';
import Checkbox from "../components/checkbox";
import { fetch } from '../utils/api-wrapper';
import ListHeader from "../components/list-header";
import ListItemDisplay from "../components/list-item-display";
import { canDeleteReport, reportEditLockErrorKey } from "../utils/report-edit-lock";
import { reportExpenseDeletionErrorKey } from "../utils/report-expense-deletion";
import AsyncSelect from 'react-select/async';
import PaginationControls from "../components/pagination-controls";
import ExpensesAccountsFilterModal from "../components/expenses-accounts-filter-modal";
import PaymentStatusCell from "../components/payment-status-cell";
import {
    EXPENSE_REPORT_COLUMNS, buildListQuery, isActiveFilter, loadVisibleColumns, nextSorting, reportStatusLabel, saveVisibleColumns,
} from "../utils/expenses-accounts-columns";

// The gear stays in view while the table scrolls sideways.
const STICKY_RIGHT = { position: "sticky", right: 0, background: "white", zIndex: 1 };

const ExpensesAccount = ({ data }) => {
    const history = useNavigate();
    const source = axios.CancelToken.source();
    const [expensesAccounts, setExpensesAccounts] = useState([]);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(50);
    const [totalRowCount, setTotalRowCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [sorting, setSorting] = useState([]);
    const [filters, setFilters] = useState({});
    const [visibleColumns, setVisibleColumns] = useState(loadVisibleColumns);
    const [columnsDraft, setColumnsDraft] = useState(null);
    const [filterColumn, setFilterColumn] = useState(null);
    const [xpensesDeleteSelection, setXpensesDeleteSelection] = useState([]);
    const [xpensesDeleteConfirm, setXpensesDeleteConfirm] = useState(false);
    const [savingError, setSavingError] = useState(null);
    const [openCreateAccountModal, setOpenCreateAccountModal] = useState(false);
    const [newAccountDescription, setNewAccountDescription] = useState("");
    const [selectedAssociate, setSelectedAssociate] = useState({value: data.id_user, label: `${data.firstName} ${data.lastName}`});
    const [associates, setAssociates] = useState(null);

    // XPEN-3794: only a report not yet submitted can be deleted.
    const deletableAccounts = () => (expensesAccounts || []).filter(canDeleteReport);

    const toggleAllXpensesSelected = () => {
        const deletable = deletableAccounts();
        xpensesDeleteSelection.length === deletable.length ? setXpensesDeleteSelection([]) : setXpensesDeleteSelection(deletable.map(x => x._id.$oid));
    }
    const isXpenseSelected = (xpens) => {
        return xpensesDeleteSelection.find(x => x == xpens._id.$oid) != undefined;
    }

    const loadUsersAndAssociates = (search_query = "", callback = () => {
    }) => {
        const val = search_query.trim().replace(/\//g, '');
        const flts = [{ id: "search", value: val }];
        const searchData = `?filters=${encodeURIComponent(JSON.stringify(flts ?? []))}&sorting=${encodeURIComponent(JSON.stringify([]))}`;
        fetch(`/users/associates/${searchData}`, "GET", {}, (response) => {
            const receivedUsers = response?.data?.users.map(u => ({
                ...u,
                label: u?.first_name + " " + u?.last_name,
                value: u._id.$oid
            }));

            if(!associates && receivedUsers && receivedUsers.length > 0)
                setAssociates(receivedUsers);

            callback(receivedUsers);

        })
    }

    const columns = EXPENSE_REPORT_COLUMNS.filter(column => visibleColumns.includes(column.id));

    const isAllXpensesSelected = () => {
        const deletable = deletableAccounts();
        if (!deletable.length)
            return false;
        return xpensesDeleteSelection.length > 1 && xpensesDeleteSelection.length === deletable.length;
    }

    const deleteXpenses = () => {
        setSaving(true);
        fetch(`/expenses_account/`, "DELETE", { "account_ids": xpensesDeleteSelection },
            response => {
                fetchExpensesAccounts(selectedAssociate);
                setXpensesDeleteSelection([]);
                setXpensesDeleteConfirm(false);
                setSaving(false);
            }
            , (error) => {
                setSaving(false);
                setXpensesDeleteConfirm(false);
                setSavingError(data.t.read(reportEditLockErrorKey(error) ?? reportExpenseDeletionErrorKey(error)));
            }, false, source.token);
    }

    const onExpenseAccountSelectedChange = (expenseAccount) =>
        isXpenseSelected(expenseAccount) ? setXpensesDeleteSelection(xpensesDeleteSelection.filter(x => x !== expenseAccount._id.$oid)) : setXpensesDeleteSelection([...xpensesDeleteSelection, expenseAccount._id.$oid]);

    const fetchExpensesAccounts = (selectedAssociate) => {
        const associateId = selectedAssociate?.value && selectedAssociate.value !== data.id_user ? selectedAssociate.value : null;
        const searchData = buildListQuery({ page, pageSize, filters, sorting, associateId });

        fetch(`/expenses_accounts/${searchData}`, "GET", {}, (response) => {
            setExpensesAccounts(response.data.expenses_accounts);
            setLoading(false);
            setTotalRowCount(response.data.meta.totalRowCount);
        },
            (error) => {
                if (error.code === "ERR_NETWORK")
                    fetchExpensesAccounts(selectedAssociate);
                else {
                    setLoading(false);
                    setSavingError(data.t.read("expenses_accounts_list.load_error"));
                }
            }
            , false, source.token);
    }

    useEffect(() => {
        fetchExpensesAccounts(selectedAssociate);
    }, [selectedAssociate, page, pageSize, filters, sorting])

    // A new filter, sort or person changes the rows, so the list starts again at its first page.
    const applyFilter = (column, value) => {
        const { [column.id]: _replaced, ...others } = filters;
        setFilters(isActiveFilter(column, value) ? { ...others, [column.id]: value } : others);
        setFilterColumn(null);
        setPage(0);
    }

    const toggleSort = (id) => {
        setSorting(nextSorting(sorting, id));
        setPage(0);
    }

    const resetFiltersAndSort = () => {
        setFilters({});
        setSorting([]);
        setPage(0);
    }

    const saveColumns = () => {
        const chosen = EXPENSE_REPORT_COLUMNS.map(column => column.id).filter(id => columnsDraft.includes(id));
        setVisibleColumns(chosen);
        saveVisibleColumns(chosen);
        setColumnsDraft(null);
    }

    const sortIcon = (id) => {
        if (sorting[0]?.id !== id) return faSort;
        return sorting[0].desc ? faSortDown : faSortUp;
    }

    useEffect(() => {
        return () => {
            source.cancel('expenses_accounts list unmounted');
        }
    }, [])

    const createXpensesAccount = () => {
        setLoading(true);
        const payload = {description: newAccountDescription}
        if (selectedAssociate)
            payload.id_user = selectedAssociate.value
            
        fetch(  `/expenses_account/`, "POST", payload,
            response => {
                const account_id = response?.data?.expenses_account_id
                if (account_id)
                    openExpensesAccount(account_id);
            },
            error => {
                setLoading(false);
                const detail = readApiErrorCode(error);
                if (error?.response?.status !== 500 && detail) {
                    setSavingError(`${data.t.read("internal_saving_error")} : ${data.t.read(detail) ?? detail}`);
                } else {
                    setSavingError(data.t.read("internal_saving_error"));
                }
            }
            ,
            null, false, source.token)
    }

    const handleOpenNewAccount = () => {
        if (!data.isMobile) {
            setNewAccountDescription("");
            setOpenCreateAccountModal(true);
        } else {
            createXpensesAccount()
        }
    }
    const openExpensesAccount = (id) => {
        history(`/expenses_account/${id}`);
    }

    return !loading ? <div className="card p-1 mt-2 rounded-lg">
        
        <div className="card-body">
            <AsyncSelect
                    name="user"
                    loadOptions={loadUsersAndAssociates}
                    onChange={(choice) => {setSelectedAssociate(choice); setPage(0)}}
                    cacheOptions
                    defaultOptions
                    value={selectedAssociate ?? ""}
                    placeholder={data.t.read("user")}
                    styles={Utils.styleSelect}
                    classNamePrefix="select"
                    className={data.isMobile ? 'col-12 mb-2' : 'col-12 col-lg-6 col-xl-3 mb-2'}
                />
            <div className="card-title d-flex justify-content-center">
                <h2>{data.t.read("expenses_account_list")}</h2>
            </div>
            { data.isMobile ?
            <>
                <ListHeader
                    checkboxDisabled={!deletableAccounts().length}
                    isChecked={isAllXpensesSelected()}
                    onCheckboxChange={toggleAllXpensesSelected}
                    disableDeleteButton={saving || xpensesDeleteSelection.length === 0}
                    onDelete={() => setXpensesDeleteConfirm(true)}
                    onAdd={createXpensesAccount}
                    saving={saving}
                    data={data}
                />
                {expensesAccounts.map(inv =>
                    <ListItemDisplay key={`${inv._id.$oid}-list`} isSelected={isXpenseSelected(inv)} selectionDisabled={!canDeleteReport(inv)} onSelectedChange={() => onExpenseAccountSelectedChange(inv)}>
                        <div onClick={() => openExpensesAccount(inv._id.$oid)} className="flex-1 pointer d-flex align-items-center" style={{ minWidth: 0 }}>
                            <div className="ml-3 text-truncate flex-1" style={{ minWidth: 0 }}>{reportStatusLabel(inv, data)}</div>
                            <div className="text-primary font-weight-bold ml-2 flex-shrink-0 text-nowrap">{inv.BigTotalPayed && `$${inv.BigTotalPayed.toFixed(2)}`}</div>
                            <div className="text-right text-muted flex-shrink-0" style={{width: '96px'}}>{Intl.DateTimeFormat(`${data.t.lang}-US`,{year: 'numeric',  month: '2-digit', day: '2-digit'}).format(new Date(inv.created_at.$date))}</div>
                        </div>
                    </ListItemDisplay>
                )}
            </>
            : <>
            <div className="d-flex justify-content-end m-1">
                {(Object.keys(filters).length > 0 || sorting.length > 0) && (
                    <button type="button" className="mr-1 btn btn-sm btn-outline-secondary" onClick={resetFiltersAndSort}>
                        {data.t.read("reset")}
                    </button>
                )}
                <button type="button" className="mr-1 text-center pointer btn btn-sm btn-secondary btn-primary-shadow pb-2 pt-2" disabled={saving || xpensesDeleteSelection.length === 0} onClick={() => setXpensesDeleteConfirm(true)}>
                    {data.t.read("delete")}
                    {saving && <FontAwesomeIcon className="ml-2 text-secondary infinite-rotate faded" icon={faSync} />}
                </button>
                <button onClick={() => handleOpenNewAccount()} type="button"
                        className="btn btn-primary btn-primary-shadow">
                    {data.t.read("add_new")}
                </button>
            </div>
            <div className="table-responsive m-0 p-0">
                <table className="table table-hover m-0 p-0 table-borderless ">
                    <thead>
                        <tr>
                            <th><Checkbox disabled={!deletableAccounts().length} isChecked={isAllXpensesSelected()} onChange={toggleAllXpensesSelected} /></th>
                            <th><div></div></th>
                            {columns.map((column) => (
                                <th key={`header-${column.id}`} aria-sort={sorting[0]?.id === column.id ? (sorting[0].desc ? "descending" : "ascending") : "none"}>
                                    <div className="d-flex align-items-center text-nowrap">
                                        <button type="button" className="btn btn-link p-0 text-reset font-weight-bold" onClick={() => toggleSort(column.id)}>
                                            {data.t.read(column.label)}
                                            <FontAwesomeIcon icon={sortIcon(column.id)} className="ml-1" />
                                        </button>
                                        <button
                                            type="button"
                                            className={`btn btn-link p-0 ml-2 ${filters[column.id] !== undefined ? "text-primary" : "text-muted"}`}
                                            aria-label={`${data.t.read("filter_by")} ${data.t.read(column.label)}`}
                                            aria-pressed={filters[column.id] !== undefined}
                                            onClick={() => setFilterColumn(column)}
                                        >
                                            <FontAwesomeIcon icon={faFilter} />
                                        </button>
                                    </div>
                                </th>
                            ))}
                            <th style={{ ...STICKY_RIGHT, width: "30px", textAlign: "center" }}>
                                <button type="button" className="btn btn-link p-0 text-secondary" aria-label={data.t.read("table_config_title")} onClick={() => setColumnsDraft(visibleColumns)}>
                                    <FontAwesomeIcon icon={faCog} />
                                </button>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {expensesAccounts.map(inv => <tr key={`${inv._id.$oid}-list`}>
                            <td><Checkbox isChecked={isXpenseSelected(inv)} disabled={!canDeleteReport(inv)} title={canDeleteReport(inv) ? undefined : data.t.read("report_edit_lock.delete_report")} onChange={() => onExpenseAccountSelectedChange(inv)} /></td>
                            <td onClick={() => openExpensesAccount(inv._id.$oid)}>
                                {inv.status === "Pending" && <FontAwesomeIcon icon={faPencilAlt} />}
                                {["Submitted", "Need_Approval"].includes(inv.status) && <FontAwesomeIcon icon={faEllipsisH} style={{ color: 'blue' }} />}
                                {inv.status === "Validated" && <FontAwesomeIcon icon={faCheck} className='text-primary' />}
                                {inv.status === "Approved" && <FontAwesomeIcon icon={faCheckCircle} className='text-primary' />}
                                {inv.status === "Denied" && <FontAwesomeIcon icon={faCheckCircle} className='text-danger' />}
                            </td>
                            {
                                columns.map(column => <td onClick={() => openExpensesAccount(inv._id.$oid)} key={`${inv._id.$oid}-${column.id}-value`}>
                                    {column.id === 'payment' ? <PaymentStatusCell invoice={{ payment: inv.payment }} t={data.t} /> : column.evaluate(inv, data)}
                                </td>)}
                            <td className="sticky-right" style={STICKY_RIGHT} onClick={() => openExpensesAccount(inv._id.$oid)} />
                        </tr>
                        )}
                    </tbody>
                </table>
            </div></>}
            {totalRowCount > 0 && (
                <PaginationControls
                    page={page + 1}
                    totalPages={Math.max(1, Math.ceil(totalRowCount / pageSize))}
                    indexOfFirstItem={page * pageSize}
                    indexOfLastItem={Math.min((page + 1) * pageSize, totalRowCount) - 1}
                    handlePageChange={(next) => setPage(next - 1)}
                    totalItems={totalRowCount}
                    data={data}
                    rowsPerPage={pageSize}
                    handleRowsPerPageChange={(size) => { setPageSize(size); setPage(0); }}
                />
            )}
        </div>
            {
                filterColumn &&
                <ExpensesAccountsFilterModal
                    column={filterColumn}
                    value={filters[filterColumn.id]}
                    data={data}
                    onApply={(value) => applyFilter(filterColumn, value)}
                    onCancel={() => setFilterColumn(null)}
                />
            }
            {
                columnsDraft &&
                <Modal title={data.t.read("table_config_title")} cancelAction={() => setColumnsDraft(null)} okAction={saveColumns} invalid={columnsDraft.length === 0} okText={data.t.read("save")} cancelText={data.t.read("cancel")}>
                    {data.t.read("table_config_content")}
                    <div className="mt-4">
                        {EXPENSE_REPORT_COLUMNS.map(column => (
                            <div key={column.id}>
                                <label>
                                    <input
                                        type="checkbox"
                                        className="mr-2"
                                        checked={columnsDraft.includes(column.id)}
                                        onChange={() => setColumnsDraft(columnsDraft.includes(column.id) ? columnsDraft.filter(id => id !== column.id) : [...columnsDraft, column.id])}
                                    />
                                    {data.t.read(column.label)}
                                </label>
                            </div>
                        ))}
                    </div>
                </Modal>
            }
            {
                openCreateAccountModal &&
                <Modal title={data.t.read("new_expenses_account")} width={600} height={400} centered cancelAction={() => {
                    setNewAccountDescription("");
                    setOpenCreateAccountModal(false);
                }} okAction={() => {
                    setNewAccountDescription("");
                    createXpensesAccount();
                }} okText={data.t.read("add")} cancelText={data.t.read("cancel")}>
                    <form onSubmit={e => e.preventDefault()}>
                        <textarea className={"form-control form-control-sm"} value={newAccountDescription}
                                  onChange={e => setNewAccountDescription(e.target.value)}
                                  placeholder={data.t.read("description")}
                                  style={{minHeight: "150px", fontSize: "1.15rem"}}/>
                    </form>

                </Modal>
            }
        {
            xpensesDeleteConfirm &&
            <Modal title={data.t.read("confirm")} cancelAction={() => setXpensesDeleteConfirm(false)} okAction={() => deleteXpenses()} okText={data.t.read("delete")} cancelText={data.t.read("cancel")}>
                {data.t.read("delete")} ?
            </Modal>
        }
        {
            savingError != null &&
            <Modal title={data.t.read("error")} okAction={() => setSavingError(null)} okText={"ok"}>
                <div className="text-danger">
                    {savingError}
                </div>
            </Modal>
        }
    </div>
        :
        <div className="d-flex loading-container">
            <FontAwesomeIcon className="infinite-rotate" icon={faSpinner} />
        </div>
}

export default inject('data')(observer(ExpensesAccount));
